import type { OneModule } from '@one/module-sdk';
import type { ManifestEntry } from './types';
import { cacheSchluessel, gehoertZuModul } from './version';

/** Contract-Majors, die diese Shell mounten kann (N und N−1, Konzept Kap. 02 §2.3). */
const SUPPORTED_CONTRACTS = new Set(['1']);

/**
 * Zwischenspeicher je Modul **und Version** (`id@version`, P10): Seit die API Module ohne
 * Neustart tauscht, kann nach einem Neuladen des Manifests ein anderes Bundle gelten.
 */
const cache = new Map<string, Promise<OneModule>>();

/**
 * Lädt ein Modul-Bundle: fetch → SRI-Prüfung → Import von der geprüften URL.
 * Natives import(url) kennt kein integrity-Attribut, deshalb prüft der Loader
 * den Hash selbst (Konzept Kap. 02 §2.1 Punkt 4).
 *
 * Bewusst **keine** Blob-URL für den eigentlichen `import()` (früher der Fall):
 * Chromium löst bare specifier eines Moduls, das über eine `blob:`-URL geladen
 * wird, nicht zuverlässig gegen die Import Map der Seite auf — beobachtet als
 * „Failed to resolve module specifier 'react/jsx-runtime'" trotz korrekter
 * Import Map und korrekt ausgeliefertem Vendor-Bundle (14.09.2026). Stattdessen
 * wird nach erfolgreicher Prüfsummen-Prüfung direkt von `entry.url` importiert
 * — die Bytes sind zu diesem Zeitpunkt bereits verifiziert, ein erneuter Abruf
 * derselben, versionierten (unveränderlichen) Adresse liefert praktisch
 * garantiert denselben Inhalt.
 */
export function loadModule(entry: ManifestEntry): Promise<OneModule> {
  const schluessel = cacheSchluessel(entry);
  const cached = cache.get(schluessel);
  if (cached) return cached;
  // Ältere Versionen desselben Moduls gibt das Manifest nicht mehr her: freigeben.
  for (const k of [...cache.keys()]) if (gehoertZuModul(k, entry.id)) cache.delete(k);
  const promise = doLoad(entry).catch((err) => {
    cache.delete(schluessel); // fehlgeschlagene Ladeversuche nicht einfrieren
    throw err;
  });
  cache.set(schluessel, promise);
  return promise;
}

async function doLoad(entry: ManifestEntry): Promise<OneModule> {
  if (!SUPPORTED_CONTRACTS.has(entry.contractVersion)) {
    throw new Error(
      `Modul '${entry.id}' braucht Contract ${entry.contractVersion}; ` +
        `diese Shell unterstützt ${[...SUPPORTED_CONTRACTS].join(', ')}.`,
    );
  }
  if (!entry.integrity?.startsWith('sha384-')) {
    throw new Error(`Modul '${entry.id}': Manifest-Eintrag ohne gültigen integrity-Hash.`);
  }

  const response = await fetch(entry.url);
  if (!response.ok) {
    throw new Error(`Bundle für '${entry.id}' nicht ladbar (HTTP ${response.status}).`);
  }
  const bytes = await response.arrayBuffer();

  const digest = await crypto.subtle.digest('SHA-384', bytes);
  const actual = `sha384-${toBase64(digest)}`;
  if (actual !== entry.integrity) {
    throw new Error(
      `Integritätsprüfung für '${entry.id}' fehlgeschlagen — Bundle entspricht nicht dem Manifest.`,
    );
  }

  const imported = (await import(/* @vite-ignore */ entry.url)) as { default?: OneModule };
  const mod = imported.default;
  if (!mod || mod.id !== entry.id || typeof mod.mount !== 'function') {
    throw new Error(
      `Bundle für '${entry.id}' exportiert kein gültiges OneModule (id/mount fehlen).`,
    );
  }
  return mod;
}

function toBase64(buffer: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary);
}
